import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { AppUserType } from '@/types';

interface UseUserTypeResult {
  userType: AppUserType | null;
  isGuest: boolean;
  isOrganizer: boolean;
  /** First ACTIVE event_access row for the user, used to deep-link guests. */
  accessibleEventId: string | null;
  loading: boolean;
}

interface UntypedClient {
  from: (t: string) => {
    select: (cols: string) => {
      eq: (col: string, value: unknown) => {
        maybeSingle: () => Promise<{ data: { user_type?: AppUserType } | null; error: unknown }>;
        order: (col: string, opts?: { ascending?: boolean }) => {
          limit: (n: number) => {
            maybeSingle: () => Promise<{
              data: { evento_id?: string } | null;
              error: unknown;
            }>;
          };
        };
      };
    };
  };
}

/**
 * Reads `public.users.user_type` for the logged-in user. For guests, also
 * resolves the event they should land on after login.
 *
 * Loads once per signed-in user. Returns sane defaults while loading so the
 * common "ORGANIZER" path doesn't get a flash of guest UI.
 */
export function useUserType(): UseUserTypeResult {
  const { user, loading: authLoading } = useAuth();
  const [userType, setUserType] = useState<AppUserType | null>(null);
  const [accessibleEventId, setAccessibleEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id) {
      setUserType(null);
      setAccessibleEventId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = supabase as unknown as UntypedClient;

    (async () => {
      setLoading(true);

      const { data: userRow } = await client
        .from('users')
        .select('user_type')
        .eq('user_id', user.id)
        .maybeSingle();
      const type: AppUserType =
        (userRow?.user_type as AppUserType | undefined) ?? 'ORGANIZER';
      if (cancelled) return;
      setUserType(type);

      if (type === 'GUEST') {
        const { data: access } = await client
          .from('event_access')
          .select('evento_id')
          .eq('invited_user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        setAccessibleEventId(access?.evento_id ?? null);
      } else {
        setAccessibleEventId(null);
      }

      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  return {
    userType,
    isGuest: userType === 'GUEST',
    isOrganizer: userType === 'ORGANIZER',
    accessibleEventId,
    loading,
  };
}
